<template>
  <div style="max-width: 450px; margin: 0 auto;">
    <h4 class="text-center">Visits by Operating System</h4>
    <Doughnut v-if="chartData" :data="chartData" :options="chartOptions" />
    <div v-else>Loading...</div>
  </div>
</template>


<script setup>
import { ref, onMounted } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import axios from 'axios';

Chart.register(ArcElement, Tooltip, Legend);

const chartData = ref(null);
/**
 * Configuration options for a chart.
 *
 * @typedef {Object} chartOptions
 * @property {boolean} responsive - Indicates whether the chart should resize responsively.
 * @property {Object} plugins - Configuration for chart plugins.
 * @property {Object} plugins.legend - Settings for the chart's legend.
 * @property {boolean} plugins.legend.display - Determines whether the legend is displayed.
 * @property {string} plugins.legend.position - Specifies the legend's position.
 * @property {Object} plugins.title - Settings for the chart's title.
 * @property {boolean} plugins.title.display - Determines whether the title is displayed.
 * @property {string} plugins.title.text - The text content of the chart's title.
 */
const chartOptions = {
  responsive: true,
  plugins: {
    legend: { display: true, position: 'bottom' },
    title: { display: true, text: 'Visits by Operating System' }
  }
};

/**
 * An array of hex color strings representing a selection of predefined colors.
 * Comments within the array indicate additional colors that are currently commented out.
 */
const colors = [
  '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545'
];

onMounted(/**
 * Fetches and processes data for visited links and updates chart data.
 * Retrieves data from the API endpoint using the stored authentication token.
 * Processes the response to compute the count of visited links by their source (so).
 * Updates the `chartData` object with labels and datasets for visualization.
 * If an error occurs or the response is invalid, `chartData` is reset to empty values.
 *
 * Environment Variables:
 * - VUE_APP_API_URL: Base URL for the API.
 *
 * Data Sources:
 * - `res.data.linksVisited`: Array containing visited link data, each having a property `so` (source).
 *
 * Error Handling:
 * - If the API call fails or data structure is invalid, the chart is updated with empty data.
 *
 * Dependencies:
 * - axios: For making the HTTP GET request.
 * - sessionStorage: To retrieve the authentication token.
 * - chartData: A reactive data object for storing chart configuration.
 * - colors: An array of colors for chart bars.
 */
async () => {
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');

  try {
    const res = await axios.get(`${apiUrl}/linksvisited`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const so = item?.so || 'Unknown';
        counts[so] = (counts[so] || 0) + 1;
      });
      const labels = Object.keys(counts);
      chartData.value = {
        labels,
        datasets: [{
          label: 'Visits',
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
          data: Object.values(counts)
        }]
      };
    } else {
      chartData.value = {
        labels: [],
        datasets: [{ label: 'Visits', data: [] }]
      };
    }
  } catch (e) {
    chartData.value = {
      labels: [],
      datasets: [{ label: 'Visits', data: [] }]
    };
  }
});
</script>
